<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { UiButton, UiInfoBox, UiText, useApi, useSharedLink } from '@shopexperts/shared';
import ReviewForm, { type ReviewFormValues } from '@/app/components/reviews/ReviewForm.vue';
import type { ExpertData } from '@/app/components/reviews/ExpertDetailsCard.vue';
import LoginForm, { type LoginAuthResult } from '@/app/components/leads/LoginForm.vue';
import { useAuthCheck } from '@/app/composables/leads/useAuthCheck';
import { useSubmitOrganicReview } from '@/app/composables/reviews/useSubmitOrganicReview';
import { resolveEmbedParentOrigin } from '@/app/composables/claim/websiteOrigin';

interface DirectReviewExpertSummary {
  id: number;
  slug: string;
  name: string;
  type: 'agency' | 'freelancer';
  role?: string | null;
  avatar?: string | null;
}

interface DirectReviewLinkData {
  expert: DirectReviewExpertSummary | null;
}

type Step = 'loading' | 'review' | 'login' | 'forbidden' | 'unavailable';

const route = useRoute();
const api = useApi();
const loginFormRef = useTemplateRef<InstanceType<typeof LoginForm>>('loginFormRef');
const websiteOrigin = resolveEmbedParentOrigin(route.query.parentOrigin);

const channelId = computed(() => {
  const value = route.query.channelId;
  return typeof value === 'string' && value ? value : 'direct-review';
});

const { data: linkData, connected, emitEvent } = useSharedLink<DirectReviewLinkData>({
  name: 'shopexperts-direct-review',
  channelId: channelId.value,
  mode: 'bidirectional',
  targetOrigin: websiteOrigin,
  default: { expert: null },
});

const expertSlug = computed(() => linkData.value.expert?.slug ?? '');
const {
  data: profile,
  isLoading: isLoadingProfile,
  error: profileError,
} = useQuery({
  queryKey: computed(() => ['public-expert-profile', expertSlug.value] as const),
  enabled: computed(() => expertSlug.value.length > 0),
  retry: false,
  queryFn: () => api.public.expert.getProfile({ params: { slug: expertSlug.value } }),
});

const {
  isAuthenticated,
  isClient,
  isLoading: isLoadingAuth,
  refetch: refetchAuth,
} = useAuthCheck();

const pendingValues = ref<ReviewFormValues | null>(null);
const draftValues = ref<ReviewFormValues | undefined>();
const forbiddenAccount = ref(false);
const loginError = ref('');

const expertData = computed<ExpertData | null>(() => {
  if (!profile.value) return null;
  return {
    name: profile.value.name,
    slug: profile.value.slug,
    type: profile.value.type === 'agency' ? 'agency' : 'freelancer',
    profile_picture: profile.value.avatar,
    role: profile.value.title,
  };
});

const canReceiveReviews = computed(() => (
  profile.value as (typeof profile.value & { can_receive_reviews?: boolean }) | undefined
)?.can_receive_reviews === true);
const isAuthenticatedClient = computed(() => isAuthenticated.value && isClient.value);

const {
  submitReview,
  isSubmitting,
  isSuccess,
  errorMessage,
} = useSubmitOrganicReview(() => profile.value?.id);

const step = computed<Step>(() => {
  if (!connected.value || !expertSlug.value || isLoadingProfile.value || isLoadingAuth.value) return 'loading';
  if (profileError.value || !expertData.value || !canReceiveReviews.value) return 'unavailable';
  if (forbiddenAccount.value || (isAuthenticated.value && !isClient.value)) return 'forbidden';
  if (pendingValues.value && !isAuthenticatedClient.value) return 'login';
  return 'review';
});

function emitResize() {
  requestAnimationFrame(() => {
    const height = Math.ceil(document.body.scrollHeight || document.documentElement.scrollHeight || 760);
    emitEvent('resize', { height });
  });
}

function emitSubmissionError(message: string) {
  emitEvent('submission-error', { message });
}

async function submit(values: ReviewFormValues) {
  try {
    await submitReview({
      quality: values.quality,
      communication: values.communication,
      timelines: values.timelines,
      value_for_money: values.value_for_money,
      review_text: values.review_text,
      is_anonymous: values.is_anonymous,
      project_value_range_id: values.project_value_range_id ?? undefined,
    });
    emitEvent('submitted', { expert_id: profile.value?.id });
  } catch {
    await nextTick();
    if (errorMessage.value) emitSubmissionError(errorMessage.value);
  } finally {
    emitResize();
  }
}

async function handleSubmit(values: ReviewFormValues) {
  draftValues.value = { ...values };
  if (!isAuthenticatedClient.value) {
    if (isAuthenticated.value) {
      forbiddenAccount.value = true;
      emitSubmissionError('Only client accounts can submit direct reviews.');
      return;
    }
    pendingValues.value = { ...values };
    loginError.value = '';
    await nextTick();
    await loginFormRef.value?.focusPassword();
    emitResize();
    return;
  }
  await submit(values);
}

async function handleAuthenticated(data: LoginAuthResult) {
  if (data.type !== 'client') {
    forbiddenAccount.value = true;
    pendingValues.value = null;
    emitSubmissionError('Only client accounts can submit direct reviews.');
    return;
  }

  await refetchAuth();
  const values = pendingValues.value;
  pendingValues.value = null;
  if (values) await submit(values);
}

function handleLoginError(message: string) {
  loginError.value = message || 'Invalid email or password.';
  emitSubmissionError(loginError.value);
  emitResize();
}

function handleBackToReview() {
  pendingValues.value = null;
  loginError.value = '';
}

function handleCancel() {
  if (isSubmitting.value) return;
  emitEvent('cancel', { expert_slug: expertSlug.value });
}

watch([step, isSubmitting, isSuccess, errorMessage], emitResize);

onMounted(() => {
  emitEvent('ready', { channelId: channelId.value });
  emitResize();
});
</script>

<template>
  <main class="bg-white font-archivo text-se-dark">
    <section class="relative mx-auto flex w-full max-w-[700px] flex-col gap-5 overflow-hidden rounded-lg bg-white p-6">
      <div
        v-if="step === 'loading'"
        class="flex min-h-[360px] flex-col items-center justify-center gap-3"
        role="status"
        data-testid="direct-review-loading-state"
      >
        <div class="size-8 animate-spin rounded-full border-2 border-se-gray-300 border-t-se-dark" />
        <UiText as="p" variant="p" text="Loading review form..." />
      </div>

      <UiInfoBox
        v-else-if="step === 'unavailable'"
        variant="error"
        title="This expert is not accepting direct reviews"
      >
        <UiButton variant="outline" label="Close" @click="handleCancel" />
      </UiInfoBox>

      <UiInfoBox
        v-else-if="step === 'forbidden'"
        variant="error"
        title="A client account is required"
      >
        <UiText as="p" variant="p-small" text="Only client accounts can submit direct reviews." />
        <UiButton variant="outline" label="Close" @click="handleCancel" />
      </UiInfoBox>

      <section v-else-if="step === 'login'" class="flex flex-col gap-5" data-testid="direct-review-login-step">
        <div class="flex flex-col gap-2">
          <UiText as="h1" variant="h3" text="Sign in to submit your review" />
          <UiText
            as="p"
            variant="p"
            text="Your completed review is preserved. Sign in with a client account to submit it for moderation."
          />
        </div>
        <UiInfoBox v-if="loginError" variant="error" title="Sign in failed">
          <UiText as="p" variant="p-small" :text="loginError" />
        </UiInfoBox>
        <LoginForm
          ref="loginFormRef"
          :show-toast="false"
          :disabled="isSubmitting"
          @authenticated="handleAuthenticated"
          @error="handleLoginError"
        />
        <footer class="flex items-center justify-between gap-4">
          <UiButton type="button" variant="outline" label="Back to review" @click="handleBackToReview" />
          <UiButton type="button" variant="primary" label="Sign in and submit" @click="loginFormRef?.triggerSubmit()" />
        </footer>
      </section>

      <section v-else data-testid="embedded-direct-review-form">
        <UiInfoBox v-if="errorMessage" variant="error" title="Review could not be submitted" class="mb-4">
          <UiText as="p" variant="p-small" :text="errorMessage" />
        </UiInfoBox>
        <ReviewForm
          v-if="expertData"
          :expert="expertData"
          :show-close-button="false"
          :is-submitting="isSubmitting"
          :is-success="isSuccess"
          :initial-values="draftValues"
          title="Review **this expert**"
          description="The expert is locked from the public profile you selected. Complete every rating and add details about your experience."
          success-title="Review submitted for moderation"
          success-description="Thank you. Your review is pending moderation and will appear publicly only after approval."
          @submit="handleSubmit"
        />
      </section>
    </section>
  </main>
</template>
